/**
 * Process roles — a named APPROVAL SEAT and the people who fill it.
 *
 * WHAT THIS PAGE USED TO BE, AND WHY IT WAS REBUILT (2026-08-10)
 * -------------------------------------------------------------
 * It was a permission editor: two tabs, and the second offered 25 checkboxes
 * (`process.create`, `hr.approve`, `budget.view`, `it.security`, `regional.approve`, …)
 * grouped under "Process Management" / "HR Management" / "Finance Management".
 *
 * None of those codes existed. The backend's vocabulary is 18 codes on the other side of a
 * dot (`requests.approve`, `tasks.complete`, `instances.read_own`, …) and the overlap with
 * the frontend's list was ZERO — not one string in common. No endpoint ever listed available
 * permissions; the list was a module-scope constant somebody wrote here. So:
 *
 *   • opening a seeded role showed every box UNCHECKED, which is what the owner reported.
 *     The list said "6 permissions" because it counted the array; the boxes were keyed on
 *     names the array never contained;
 *   • ticking one appended a fictional code to a real array;
 *   • the stats line's "25 Available Permissions" counted a frontend constant.
 *
 * And underneath, `ProcessRole.permissions` was not a permission field at all.
 * `resolvers.py` read that same column looking for `wrk_…` ids to decide who a role-assigned
 * task goes to. Both writers used one column for incompatible purposes, so all six seeded
 * roles held codes, held zero worker ids, and every role-assigned step raised "No members
 * assigned" — 100% of the time in production, not an edge case.
 *
 * WHAT IT IS NOW
 * --------------
 * The page's own subtitle always described the right thing: *"Define who can be assigned to
 * process tasks."* That is membership, and membership is what the engine reads. So the seat
 * carries a name, a description and the people in it — nothing else.
 *
 * Permissions are deliberately absent, not relocated. "What may you do in this app" already
 * has an owner: Settings → Access control, backed by `system.Role`/`Permission`/
 * `RolePermission` and enforced by `require_permission` on every endpoint. A second
 * permission model here would duplicate an enforced one with an unenforced one.
 *
 * THE TABS WENT WITH THE PERMISSIONS, AND THAT IS THE ALIGNMENT FIX
 * ----------------------------------------------------------------
 * The dialog had `<Tabs>` + a `TabPanel` whose body was `<Box sx={{ p: 3 }}>`, inside a
 * `DialogContent` the theme already gutters at `DIALOG_GUTTER` (svdy-ui §17). That stacked
 * five different left origins in one dialog — title 24, tab label 40, fields 48, group
 * header 48, checkbox ~46, label text ~79 — which is what "新增的 role ui 都不对齐" was.
 * The tabs existed only to hold the permissions tab. One purpose, one form, no tabs, and the
 * axis comes from the theme rather than from a call site.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  GroupAdd as GroupAddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { ProcessRole } from './data';
import { ConfirmDialog } from '../../../components/primitives';
import { ApiError, organizationApi, processesApi } from '../../../api';
import { formatProcessDate, getProcessRoleDisplay, translateRoleCategory } from '../processDisplay';
import { translateApiError } from '../../../i18n/translateApiError';
import type {
  ProcessRole as ProcessRoleDTO,
  ProcessRoleCreate,
  ProcessRoleUpdate,
} from '../../../api/processes';

/** A worker, reduced to what a picker row needs. */
interface PickableWorker {
  id: string;
  name: string;
  code: string;
  department: string | null;
}

function toFront(r: ProcessRoleDTO): ProcessRole {
  const cat = (r.category ?? 'custom') as ProcessRole['category'];
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? '',
    category: cat,
    memberWorkerIds: Array.isArray(r.member_worker_ids) ? (r.member_worker_ids as string[]) : [],
    // From the server, which counts only ACTIVE members. Deliberately not
    // `memberWorkerIds.length`: a seat whose only occupant has left reads 0 here while the
    // picker still shows the name, and that gap is the state an admin must act on — the
    // engine treats such a seat as unstaffed.
    assignedWorkers: r.assigned_workers ?? 0,
    isActive: r.is_active ?? true,
    // `ProcessRoleResponse` has no `updated_at`. It used to be surfaced in a column headed
    // "Last Modified" while holding `created_at`, which is a caption that contradicts its
    // own number; the column now says Created.
    createdAt: (r.created_at ?? '').slice(0, 10),
  };
}

const ProcessRoles: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [roles, setRoles] = useState<ProcessRole[]>([]);
  const [workers, setWorkers] = useState<PickableWorker[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<ProcessRole | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'custom' as ProcessRole['category'],
    isActive: true,
  });
  const [memberIds, setMemberIds] = useState<string[]>([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The workers list is loaded ALONGSIDE the roles rather than searched per keystroke.
      // An SMB has tens of people, not thousands, so a debounced server search would be
      // machinery for a problem this product does not have — and the picker can then show a
      // name for an id already on the role without a second round trip.
      const [roleRows, workerRows] = await Promise.all([
        processesApi.processRolesApi.list(),
        organizationApi.workersApi.list('active'),
      ]);
      setRoles(roleRows.map(toFront));
      setWorkers(
        (workerRows as any[]).map((w) => ({
          id: w.id,
          name: [w.first_name, w.last_name].filter(Boolean).join(' ') || w.worker_code,
          code: w.worker_code ?? '',
          department: w.primary_department_name ?? null,
        })),
      );
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('processes:roles.loadError'));
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const workerById = useMemo(
    () => new Map(workers.map((w) => [w.id, w])),
    [workers],
  );

  /** A member id we can no longer name — the worker was removed from the org. */
  const nameFor = useCallback(
    (id: string) => workerById.get(id)?.name ?? t('processes:roles.formerWorker'),
    [workerById, t],
  );

  const filteredRoles = useMemo(() => {
    let current = roles;
    if (searchTerm) {
      const normalized = searchTerm.toLowerCase();
      current = current.filter((role) => {
        const display = getProcessRoleDisplay(t, role);
        return display.name.toLowerCase().includes(normalized) ||
          display.description.toLowerCase().includes(normalized);
      });
    }
    if (filterCategory !== 'all') {
      current = current.filter((role) => role.category === filterCategory);
    }
    if (filterStatus !== 'all') {
      current = current.filter((role) => role.isActive === (filterStatus === 'active'));
    }
    return current;
  }, [roles, searchTerm, filterCategory, filterStatus, t]);

  /**
   * The stats line, and what it deliberately does NOT count.
   *
   * "25 Available Permissions" is gone: it counted a hardcoded frontend array, so it was a
   * fact about this file rather than about the customer's data. "0 Process Usage" is gone
   * too — it was `processCount: 0`, hardcoded in the adapter, so the column could only ever
   * read zero.
   *
   * `unstaffed` replaces them, because it is the only number here an admin can act on: a
   * seat with nobody in it makes every request routed to it fail on submission.
   */
  const stats = useMemo(() => {
    const staffed = roles.filter((r) => r.assignedWorkers > 0).length;
    return {
      total: roles.length,
      staffed,
      unstaffed: roles.filter((r) => r.isActive && r.assignedWorkers === 0).length,
    };
  }, [roles]);

  const categoriesInUse = useMemo(
    () => Array.from(new Set(roles.map((r) => r.category))).sort(),
    [roles],
  );

  const resetForm = () => {
    setFormData({ name: '', description: '', category: 'custom', isActive: true });
    setMemberIds([]);
    setSelectedRole(null);
  };

  const handleCreateRole = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      const payload: ProcessRoleCreate = {
        name: formData.name,
        description: formData.description || null,
        // `category` is NOT sent. The API narrowed it to `Literal["custom"]` on 2026-09-09, because a
        // caller-supplied `system` minted a row that `update_role` and `delete_role` both answer 403
        // for — un-editable and un-removable in the admin's own picker, with no path back. This dialog
        // could only ever produce `custom` anyway: the `system` MenuItem is disabled, and the Edit
        // button is not rendered for a system seat. So the field held one value and is now derived
        // server-side — create defaults to `custom`, and `_update` uses `exclude_unset`, so omitting it
        // leaves an existing category untouched.
        member_worker_ids: memberIds,
      };
      await processesApi.processRolesApi.create(payload);
      setToast(t('processes:roles.roleCreated', { name: formData.name }));
      setCreateDialogOpen(false);
      resetForm();
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('processes:roles.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditRole = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      const payload: ProcessRoleUpdate = {
        name: formData.name,
        description: formData.description || null,
        // See the create payload above: `category` is server-derived now.
        is_active: formData.isActive,
      };
      await processesApi.processRolesApi.update(selectedRole.id, payload);
      // Membership goes through its own endpoint, because it is allowed on seats whose name
      // is not editable. Sent second so a rejected rename does not silently change who is
      // on the seat.
      await processesApi.processRolesApi.setMembers(selectedRole.id, memberIds);
      setToast(t('processes:roles.roleUpdated', { name: formData.name }));
      setEditDialogOpen(false);
      resetForm();
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  /** Membership only — the path a SYSTEM seat uses, where the name is not the admin's. */
  const handleSaveMembers = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await processesApi.processRolesApi.setMembers(selectedRole.id, memberIds);
      setToast(t('processes:roles.membersUpdated', {
        name: getProcessRoleDisplay(t, selectedRole).name,
      }));
      setMembersDialogOpen(false);
      resetForm();
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;
    try {
      await processesApi.processRolesApi.delete(selectedRole.id);
      setToast(t('processes:roles.roleDeleted', { name: selectedRole.name }));
      setDeleteDialogOpen(false);
      setSelectedRole(null);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.deleteFailed'));
      throw e; // ConfirmDialog keeps itself open on throw
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const openEditDialog = (role: ProcessRole) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      category: role.category,
      isActive: role.isActive,
    });
    setMemberIds(role.memberWorkerIds);
    setEditDialogOpen(true);
  };

  const openMembersDialog = (role: ProcessRole) => {
    setSelectedRole(role);
    setMemberIds(role.memberWorkerIds);
    setMembersDialogOpen(true);
  };

  const openViewDialog = (role: ProcessRole) => {
    setSelectedRole(role);
    setViewDialogOpen(true);
  };

  const openDeleteDialog = (role: ProcessRole) => {
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  /**
   * The picker. One control, used by all three dialogs.
   *
   * Options are the org's ACTIVE workers. A member who has since left stays in `value` and
   * is rendered with the "former staff" label rather than dropped, because silently removing
   * somebody from the admin's list on load would hide the reason the seat stopped working.
   */
  const renderMemberPicker = () => (
    <Autocomplete
      multiple
      options={workers}
      value={memberIds.map((id) => workerById.get(id) ?? { id, name: nameFor(id), code: '', department: null })}
      getOptionLabel={(w) => w.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_e, picked) => setMemberIds(picked.map((w) => w.id))}
      renderOption={(props, w) => (
        <li {...props} key={w.id}>
          <Box>
            <Typography variant="body2">{w.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[w.code, w.department].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((w, index) => (
          <Chip
            {...getTagProps({ index })}
            key={w.id}
            label={w.name}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem', borderRadius: 1 }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('processes:roles.membersLabel')}
          placeholder={memberIds.length ? undefined : t('processes:roles.membersPlaceholder')}
          helperText={t('processes:roles.membersHelp')}
        />
      )}
    />
  );

  const renderRoleRow = (role: ProcessRole) => {
    const display = getProcessRoleDisplay(t, role);
    const isSystem = role.category === 'system';

    return (
      <TableRow key={role.id} hover>
        {/* The only column that wraps, because it is prose. A `maxWidth` bounds the
            description so it wraps at a sane measure instead of stretching the row and
            squeezing every other column into wrapping too. */}
        <TableCell sx={{ maxWidth: 340 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{display.name}</Typography>
          <Typography variant="body2" color="text.secondary">{display.description}</Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {/* §1 — small, outlined, square-ish. A filled primary-blue chip as the loudest
              thing in the row made the category compete with the role's own name. */}
          <Chip
            label={translateRoleCategory(t, role.category)}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
          />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {role.assignedWorkers > 0 ? (
            <Typography variant="body2">
              {t('processes:roles.memberCount', { count: role.assignedWorkers })}
            </Typography>
          ) : (
            // Not "0". An unstaffed seat fails every request routed to it, so it reads as
            // the problem it is — and it is the reason this column exists.
            <Typography variant="body2" color="warning.main">
              {t('processes:roles.nobodyAssigned')}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Chip
            label={role.isActive ? t('common:status.active') : t('common:status.inactive')}
            size="small"
            variant="outlined"
            color={role.isActive ? 'success' : 'default'}
            sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
          />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" color="text.secondary">
            {formatProcessDate(role.createdAt, i18n.language)}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {/* `whiteSpace: nowrap` — the actions used to wrap onto a second line inside a
              column sized for two icons, which is the ragged block visible in the owner's
              screenshot. A system seat shows two actions, a custom one shows four. */}
          <Box sx={{ whiteSpace: 'nowrap' }}>
            <Tooltip title={t('common:actions.viewDetails')}>
              <IconButton size="small" onClick={() => openViewDialog(role)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('processes:roles.manageMembers')}>
              <IconButton size="small" onClick={() => openMembersDialog(role)}>
                <GroupAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* System seats are not renamed or deleted — the API returns 403 either way, so
                rendering the buttons at all was an offer the product cannot honour. They are
                omitted rather than disabled: a greyed control still says "you may
                eventually", and this one never will. Staffing stays available above. */}
            {!isSystem && (
              <>
                <Tooltip title={t('processes:roles.editRole')}>
                  <IconButton size="small" onClick={() => openEditDialog(role)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('processes:roles.deleteTitle')}>
                  <IconButton size="small" onClick={() => openDeleteDialog(role)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  /** Name + description + active, shared by create and edit. No tabs: one purpose. */
  const renderSeatFields = () => (
    <Stack spacing={2.5}>
      <TextField
        fullWidth
        label={t('processes:roles.roleNameLabel')}
        value={formData.name}
        onChange={(event) => setFormData({ ...formData, name: event.target.value })}
        required
      />
      <TextField
        fullWidth
        label={t('processes:roles.descriptionLabel')}
        value={formData.description}
        onChange={(event) => setFormData({ ...formData, description: event.target.value })}
        multiline
        rows={2}
      />
      <FormControl fullWidth>
        <InputLabel>{t('processes:roles.categoryLabel')}</InputLabel>
        <Select
          value={formData.category}
          label={t('processes:roles.categoryLabel')}
          onChange={(event) =>
            setFormData({ ...formData, category: event.target.value as ProcessRole['category'] })
          }
        >
          {/* `system` is offered as a DISABLED option so an existing system seat renders its
              own value. Without it the Select's value matched no item and the field opened
              BLANK on a seeded role — the field reading empty on a role that plainly has a
              category. It cannot be chosen, because only the seeder creates system seats. */}
          <MenuItem value="system" disabled>{t('processes:roles.categorySystem')}</MenuItem>
          <MenuItem value="custom">{t('processes:roles.categoryCustom')}</MenuItem>
          <MenuItem value="department">{t('processes:roles.categoryDepartment')}</MenuItem>
        </Select>
      </FormControl>
      {renderMemberPicker()}
      <FormControlLabel
        control={
          <Switch
            checked={formData.isActive}
            onChange={(event) => setFormData({ ...formData, isActive: event.target.checked })}
          />
        }
        label={t('processes:roles.activeRole')}
      />
    </Stack>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('processes:roles.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('processes:roles.subtitle')}</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton size="small" onClick={fetchAll} disabled={loading}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog} size="small" disabled={saving}>
            {t('processes:roles.createRole')}
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {/* §3 — one body2 line, bold numbers, middot separators, no card chrome and no
          coloured avatars. The four icons this replaces (a blue shield, a green person, an
          info-blue tree, an orange cog) were four hues on a line whose job is to be read at
          a glance. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>{stats.total}</strong> {t('processes:roles.statTotal')}
        {' · '}
        <strong>{stats.staffed}</strong> {t('processes:roles.statStaffed')}
        {stats.unstaffed > 0 && (
          <>
            {' · '}
            <Box component="span" sx={{ color: 'warning.main' }}>
              <strong>{stats.unstaffed}</strong> {t('processes:roles.statUnstaffed')}
            </Box>
          </>
        )}
      </Typography>

      {loading && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && (
        <>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder={t('processes:roles.searchPlaceholder')}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                size="small"
                sx={{ minWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {/* Options come from the categories actually present, plus `all`. A fixed list
                  would offer `department` to a customer who has never created one — a filter
                  whose only outcome is an empty table. */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('processes:roles.categoryLabel')}</InputLabel>
                <Select
                  value={filterCategory}
                  label={t('processes:roles.categoryLabel')}
                  onChange={(event) => { setFilterCategory(event.target.value); setPage(0); }}
                >
                  <MenuItem value="all">{t('processes:roles.allCategories')}</MenuItem>
                  {categoriesInUse.map((c) => (
                    <MenuItem key={c} value={c}>{translateRoleCategory(t, c)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{t('common:status.label')}</InputLabel>
                <Select
                  value={filterStatus}
                  label={t('common:status.label')}
                  onChange={(event) => { setFilterStatus(event.target.value); setPage(0); }}
                >
                  <MenuItem value="all">{t('processes:roles.allStatus')}</MenuItem>
                  <MenuItem value="active">{t('common:status.active')}</MenuItem>
                  <MenuItem value="inactive">{t('common:status.inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Pagination lives in THIS row, to the right of the filters — 37 of the 39
                paginated admin tables put it above the table, and so did this page before the
                rewrite. A draft moved it below, which made Roles one of only two outliers.
                Verified by matching `<TableContainer[\s>]` rather than `<Table`, because
                `<Table` is a prefix of `<TableCell` and matches inside the row renderer
                hundreds of lines earlier — that prefix bug is what made an earlier check
                report the opposite.

                The sx is this page's own, kept verbatim: it collapses the toolbar's default
                min-height and side padding and stops the spacer from flexing, so the control
                stays on one line beside three filters instead of wrapping on a 13" screen.

                No `labelRowsPerPage` / `labelDisplayedRows` / `getItemAriaLabel`: `App.tsx`
                registers all three as `MuiTablePagination.defaultProps`, rebuilt when the
                language changes, and none of the other 38 tables passes them. */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredRoles.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              sx={{
                '& .MuiTablePagination-toolbar': { minHeight: 'auto', pl: 0, pr: 0 },
                '& .MuiTablePagination-spacer': { flex: 'none' },
              }}
            />
          </Box>

          {filteredRoles.length === 0 ? (
            <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                {roles.length === 0 ? t('processes:roles.emptyTitle') : t('processes:roles.noMatchTitle')}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                {roles.length === 0 ? t('processes:roles.emptyHint') : t('processes:roles.noMatchHint')}
              </Typography>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ width: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <TableContainer>
                {/* `minWidth` + per-column `whiteSpace: nowrap` on everything except the two
                    columns that legitimately wrap (the role's name/description, which is prose).
                    In the owner's screenshot four headers had broken onto two lines — "Assigned
                    Workers", "Process Usage", "Last Modified" — and so had a cell reading "4 /
                    permissions", which made every row a different height and the whole table
                    look ragged. A table narrower than its content wraps rather than scrolls
                    unless it is told otherwise; TableContainer already scrolls horizontally, so
                    `minWidth` lets it do that instead. */}
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap', fontWeight: 600 } }}>
                      <TableCell>{t('processes:roles.colName')}</TableCell>
                      <TableCell>{t('processes:roles.categoryLabel')}</TableCell>
                      <TableCell>{t('processes:roles.colMembers')}</TableCell>
                      <TableCell>{t('common:status.label')}</TableCell>
                      <TableCell>{t('processes:roles.colCreated')}</TableCell>
                      <TableCell align="right">{t('processes:roles.colActions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRoles
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map(renderRoleRow)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Create / edit. One form, no tabs — the axis is the theme's. */}
      <Dialog
        open={createDialogOpen || editDialogOpen}
        onClose={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ py: 2 }}>
          {createDialogOpen ? t('processes:roles.createNewRole') : t('processes:roles.editRole')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {renderSeatFields()}
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); resetForm(); }}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={createDialogOpen ? handleCreateRole : handleEditRole}
            disabled={saving || !formData.name.trim()}
          >
            {saving ? t('common:status.saving') : (createDialogOpen ? t('processes:roles.createRole') : t('common:actions.save'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Membership only — the path a SYSTEM seat uses. */}
      <Dialog
        open={membersDialogOpen}
        onClose={() => { setMembersDialogOpen(false); resetForm(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ py: 2 }}>
          {selectedRole
            ? t('processes:roles.manageMembersFor', { name: getProcessRoleDisplay(t, selectedRole).name })
            : t('processes:roles.manageMembers')}
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('processes:roles.manageMembersHelp')}
            </Typography>
            {renderMemberPicker()}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => { setMembersDialogOpen(false); resetForm(); }}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveMembers} disabled={saving}>
            {saving ? t('common:status.saving') : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details. Shows the seat and who is in it — the two things it has. */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ py: 2 }}>{t('processes:roles.roleDetails')}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {selectedRole && (() => {
            const display = getProcessRoleDisplay(t, selectedRole);
            return (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('processes:roles.nameLabel')}</Typography>
                  <Typography variant="body1">{display.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('processes:roles.descriptionLabel')}</Typography>
                  <Typography variant="body2">{display.description || '—'}</Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={translateRoleCategory(t, selectedRole.category)}
                    size="small" variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
                  />
                  <Chip
                    label={selectedRole.isActive ? t('common:status.active') : t('common:status.inactive')}
                    size="small" variant="outlined"
                    color={selectedRole.isActive ? 'success' : 'default'}
                    sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1 }}
                  />
                </Stack>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('processes:roles.membersLabel')}
                  </Typography>
                  {selectedRole.memberWorkerIds.length === 0 ? (
                    <Typography variant="body2" color="warning.main">
                      {t('processes:roles.nobodyAssignedHint')}
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {selectedRole.memberWorkerIds.map((id) => (
                        <Chip
                          key={id}
                          label={nameFor(id)}
                          size="small" variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem', borderRadius: 1 }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={() => setViewDialogOpen(false)}>{t('common:actions.close')}</Button>
          {selectedRole && (
            <Button
              variant="contained"
              onClick={() => { setViewDialogOpen(false); openMembersDialog(selectedRole); }}
            >
              {t('processes:roles.manageMembers')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={t('processes:roles.deleteTitle')}
        description={t('processes:roles.deleteMessage', { name: selectedRole?.name ?? '' })}
        confirmLabel={t('common:actions.delete')}
        tone="danger"
        onConfirm={handleDeleteRole}
        onClose={() => { setDeleteDialogOpen(false); setSelectedRole(null); }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ProcessRoles;
